import { LitElement, html, css } from 'lit';

// Inlined from ~/web/wc/public/components/view/base/converters.js —
// vendoring view/ wasn't worth pulling for one tiny converter.
export const InvBool = {
  fromAttribute: (v) => v !== 'false',
  toAttribute: (v) => (v ? null : 'false'),
};

export function parseVec3(s, fallback = [0, 0, 0]) {
  if (s == null || s === '') return fallback;
  const parts = String(s).trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return fallback;
  return parts;
}

export function findAncestor(el, predicate) {
  let cur = el.parentNode;
  while (cur) {
    if (cur.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      cur = cur.host;
      continue;
    }
    if (predicate(cur)) return cur;
    cur = cur.parentNode;
  }
  return null;
}

export function findThreeParent(el, skipObject3D = null) {
  let cur = el.parentNode;
  while (cur) {
    if (cur.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      cur = cur.host;
      continue;
    }
    if (
      cur.object3D &&
      typeof cur.object3D === 'object' &&
      cur.object3D !== skipObject3D
    ) {
      return cur;
    }
    cur = cur.parentNode;
  }
  return null;
}

export function collectAncestorReady(el) {
  const promises = [];
  let cur = el.parentNode;
  while (cur) {
    if (cur.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      cur = cur.host;
      continue;
    }
    const w = cur.whenReady;
    if (w && typeof w.then === 'function') promises.push(w);
    cur = cur.parentNode;
  }
  return promises;
}

export const ReadyMixin = (Base) =>
  class extends Base {
    constructor(...args) {
      super(...args);
      this.whenReady = new Promise((r) => (this._resolveReady = r));
    }
    _markReady() {
      this._resolveReady?.();
      this._resolveReady = null;
    }
  };

export const TransformMixin = (Base) =>
  class extends Base {
    static properties = {
      position: { type: String },
      rotation: { type: String },
      scale: { type: String },
    };

    constructor(...args) {
      super(...args);
      this.position = '0 0 0';
      this.rotation = '0 0 0';
      this.scale = '1 1 1';
    }
  };

export class AttachableElement extends ReadyMixin(LitElement) {
  static styles = css`:host { display: contents; }`;

  constructor() {
    super();
    this.object3D = null;
  }

  createObject3D() {
    throw new Error('createObject3D() must be implemented by subclass');
  }

  beforeAttach() {}

  async connectedCallback() {
    super.connectedCallback();
    if (!this.object3D) this.object3D = this.createObject3D();
    this.beforeAttach();
    const gates = collectAncestorReady(this);
    if (gates.length) await Promise.all(gates);
    if (!this.isConnected) return;
    const parent = findThreeParent(this, this.object3D);
    if (parent && parent.object3D && parent.object3D.add) {
      parent.object3D.add(this.object3D);
    }
    this._markReady();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.object3D && this.object3D.parent) {
      this.object3D.parent.remove(this.object3D);
    }
  }

  render() {
    return html`<slot></slot>`;
  }
}

export class WebGLElement extends TransformMixin(AttachableElement) {
  beforeAttach() {
    this.applyTransform();
  }

  updated(changed) {
    if (changed.has('position') || changed.has('rotation') || changed.has('scale')) {
      this.applyTransform();
    }
  }

  applyTransform() {
    if (!this.object3D || !this.object3D.position) return;
    const p = parseVec3(this.position);
    const r = parseVec3(this.rotation);
    const s = parseVec3(this.scale, [1, 1, 1]);
    this.object3D.position.set(p[0], p[1], p[2]);
    this.object3D.rotation.set(r[0], r[1], r[2]);
    this.object3D.scale.set(s[0], s[1], s[2]);
  }
}
